import { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Upload, X, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";

/**
 * Sprint 70 — PDF upload to object storage (publications / white-papers).
 * onChange({url, filename}): called once uploaded.
 */
export default function PdfUpload({ value, filename, onChange, label = "PDF du livre blanc", testId = "pdf-upload" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const fullUrl = value && value.startsWith("/api/") ? `${BACKEND}${value}` : value;

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error(`Type "${file.type || "inconnu"}" non supporté. PDF uniquement.`);
      if (ref.current) ref.current.value = "";
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error(`Fichier trop lourd (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 20 MB.`);
      if (ref.current) ref.current.value = "";
      return;
    }
    setBusy(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload-pdf", fd, {
        onUploadProgress: (evt) => { if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100)); },
        timeout: 90000,
      });
      onChange({ url: data.url, filename: data.filename, thumbnail_url: data.thumbnail_url || "" });
      toast.success("PDF uploadé");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Upload échoué");
    } finally {
      setBusy(false); setProgress(0);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</label>
      <input ref={ref} type="file" accept="application/pdf" onChange={onPick} className="hidden" data-testid={`${testId}-input`} />
      {fullUrl ? (
        <div className="mt-1 flex items-center justify-between gap-3 rounded-xl border-2 border-wine-200 bg-wine-50/40 p-3">
          <a href={fullUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm font-semibold text-wine-700 hover:underline truncate" data-testid={`${testId}-link`}>
            <FileText className="h-4 w-4 shrink-0" />
            <span className="truncate">{filename || "document.pdf"}</span>
          </a>
          <div className="flex gap-1 shrink-0">
            <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="rounded-full bg-white border border-wine-300 px-3 py-1 text-[11px] font-semibold text-wine-700 hover:bg-wine-100 disabled:opacity-50" data-testid={`${testId}-replace`}>
              Remplacer
            </button>
            <button type="button" onClick={() => onChange({ url: "", filename: "" })} className="rounded-full bg-white p-1.5 text-neutral-500 hover:bg-rose-50 hover:text-rose-700" data-testid={`${testId}-clear`}>
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} disabled={busy}
                className="mt-1 flex h-20 w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-wine-200 bg-wine-50/20 text-sm font-semibold text-wine-700 hover:bg-wine-50 disabled:opacity-50"
                data-testid={`${testId}-button`}>
          {busy ? (
            <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Upload… {progress > 0 ? `${progress}%` : ""}</span>
          ) : (
            <span className="inline-flex items-center gap-2"><Upload className="h-4 w-4" /> Uploader le PDF (20 MB max)</span>
          )}
        </button>
      )}
    </div>
  );
}
